import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Key, Save, Loader2 } from 'lucide-react';

export default function AdminConfigPixPage() {
  const [config, setConfig] = useState({ tipo_chave: '', chave_pix: '', nome_recebedor: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await api.get('/admin/config-pix');
      setConfig(res.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const salvar = async () => {
    if (!config.tipo_chave || !config.chave_pix) {
      toast.error('Preencha tipo e valor da chave PIX');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/admin/config-pix', config);
      setConfig(res.data);
      toast.success('Chave PIX da plataforma salva!');
    } catch { toast.error('Erro ao salvar'); }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48 bg-[#141414]" />
        <Skeleton className="h-64 bg-[#141414]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl" data-testid="admin-config-pix-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
        <Key className="w-7 h-7 inline mr-2 text-green-400" />CHAVE PIX DA PLATAFORMA
      </h1>

      <Card className="bg-[#141414] border-[#27272A]" data-testid="admin-pix-card">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg tracking-tight uppercase">Configuracao PIX</CardTitle>
          <CardDescription>Essa chave sera usada para os pagamentos dos gestores (plano da plataforma).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tipo de Chave</Label>
            <Select value={config.tipo_chave} onValueChange={(v) => setConfig(p => ({ ...p, tipo_chave: v }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="admin-pix-tipo">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="cpf">CPF</SelectItem>
                <SelectItem value="cnpj">CNPJ</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="telefone">Telefone</SelectItem>
                <SelectItem value="aleatoria">Chave Aleatoria</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor da Chave</Label>
            <Input
              placeholder="Ex: 12.345.678/0001-99"
              value={config.chave_pix}
              onChange={(e) => setConfig(p => ({ ...p, chave_pix: e.target.value }))}
              className="bg-[#0A0A0A] border-[#27272A]"
              data-testid="admin-pix-chave"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome do Recebedor (opcional)</Label>
            <Input
              placeholder="Nome que aparecera no PIX"
              value={config.nome_recebedor}
              onChange={(e) => setConfig(p => ({ ...p, nome_recebedor: e.target.value }))}
              className="bg-[#0A0A0A] border-[#27272A]"
              data-testid="admin-pix-nome"
            />
          </div>
          <Button onClick={salvar} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="admin-salvar-pix-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar Chave PIX
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
